import { useEffect, useMemo, useState } from "react";
import { LOGO } from "../lib/logo";
import {
  abaixoDoMinimo,
  AVISO_ABAIXO_DO_MINIMO,
  minimoDaMensalidade,
  PCT_ANTECIPADO_PADRAO,
  propostaVazia,
  recalcular,
  rotuloTaxaMaterial,
  taxaMaterial,
  type CamposProposta,
} from "../lib/calculos";
import { moeda } from "../lib/formato";
import { anoLetivoSugerido, sugerirEscolaridade } from "../lib/escolaridade";
import { dataBrParaIso, dataHora, dataNasc, mascaraData, soDigitos } from "../lib/formato";
import { codigoDoPais, ehBrasil, mascaraPorPais, PAISES, telefoneValido } from "../lib/paises";
import { CampoValor } from "./CampoValor";
import type { DadosApoio } from "../lib/dados";
import {
  MOTIVOS_NAO_MATRICULA,
  ROTULO_PERIODO,
  ROTULO_STATUS,
  type StatusVisita,
  type TipoRegistro,
  type Visita,
} from "../lib/tipos";

const VERDE = "#0e8c3a";
const AZUL = "#1570ef";
/** Uma cor por bloco da parte de cima — o olho acha a seção antes de ler. */
const ROSA = "#b83280";
const AMBAR = "#b54708";
const LILAS = "#6941c6";

/** Estado do formulário: tudo string, convertido na hora de enviar. */
export interface EstadoFormulario extends CamposProposta {
  nomeResponsavel: string;
  telefoneResponsavel: string;
  nomeAluno: string;
  /** Texto digitado, no formato brasileiro: "03/05/2017". */
  dataNascimento: string;
  /** Ano de referência do cálculo de série. Não vai para a API. */
  anoLetivo: string;
  cursoId: string;
  emailResponsavel: string;
  /** Código do país do telefone; "" vale Brasil. */
  paisTelefone: string;
  serieId: string;
  periodo: string;
  indicacao: string;
  ondeConheceu: string;
  nomeIrmao: string;
  serieIrmao: string;
  inicioEm: string;
  fimEm: string;
  status: StatusVisita;
  /** VISITA ou ORCAMENTO — a API ignora na edição, só vale na criação. */
  tipo: TipoRegistro;
  motivoNaoMatricula: string;
  local: string;
  descricao: string;
  diferenciaisIds: string[];
}

const agoraLocal = () => {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
};

const paraInput = (iso: string | null) => {
  if (!iso) return "";
  const d = new Date(iso);
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
};

export function estadoInicial(v?: Visita | null, tipo: TipoRegistro = "VISITA"): EstadoFormulario {
  const p = v?.proposta;
  const s = (x: unknown) => (x === null || x === undefined ? "" : String(x));
  const inicio = v ? paraInput(v.inicioEm) : agoraLocal();
  return {
    ...propostaVazia(),
    nomeResponsavel: v?.nomeResponsavel ?? "",
    paisTelefone: codigoDoPais(v?.paisTelefone),
    telefoneResponsavel: mascaraPorPais(v?.telefoneResponsavel ?? "", v?.paisTelefone),
    emailResponsavel: v?.emailResponsavel ?? "",
    nomeAluno: v?.nomeAluno ?? "",
    dataNascimento: dataNasc(v?.dataNascimento),
    anoLetivo: String(anoLetivoSugerido(inicio)),
    cursoId: v?.curso?.id ?? "",
    serieId: v?.serie?.id ?? "",
    periodo: v?.periodo ?? "",
    indicacao: v?.indicacao ?? "",
    ondeConheceu: v?.ondeConheceu ?? "",
    nomeIrmao: v?.nomeIrmao ?? "",
    serieIrmao: v?.serieIrmao ?? "",
    inicioEm: inicio,
    fimEm: paraInput(v?.fimEm ?? null),
    status: v?.status ?? "AGENDADO",
    tipo: v?.tipo ?? tipo,
    motivoNaoMatricula: v?.motivoNaoMatricula ?? "",
    local: v?.local ?? "",
    descricao: v?.descricao ?? "",
    diferenciaisIds: v?.diferenciais?.map((d) => d.diferencial.id) ?? [],
    valorNormal: s(p?.valorNormal),
    percentualDesconto: s(p?.percentualDesconto),
    valorComDesconto: s(p?.valorComDesconto),
    valorMatricula: s(p?.valorMatricula),
    percentualDescontoMatricula: s(p?.percentualDescontoMatricula),
    valorMatriculaComDesconto: s(p?.valorMatriculaComDesconto),
    qtdParcelas: s(p?.qtdParcelas),
    valorAnual: s(p?.valorAnual),
    valorNormalIntegral: s(p?.valorNormalIntegral),
    percentualDescontoIntegral: s(p?.percentualDescontoIntegral),
    valorComDescontoIntegral: s(p?.valorComDescontoIntegral),
    valorMatriculaIntegral: s(p?.valorMatriculaIntegral),
    percentualDescontoMatriculaIntegral: s(p?.percentualDescontoMatriculaIntegral),
    valorMatriculaComDescontoIntegral: s(p?.valorMatriculaComDescontoIntegral),
    qtdParcelasIntegral: s(p?.qtdParcelasIntegral),
    valorAnualIntegral: s(p?.valorAnualIntegral),
    materialCoc: s(p?.materialCoc),
    materialBilingue: s(p?.materialBilingue),
    totalMaterial: s(p?.totalMaterial),
    qtdParcelasMaterial: s(p?.qtdParcelasMaterial),
    valorParcelaMaterial: s(p?.valorParcelaMaterial),
    // proposta antiga não tem o percentual gravado — cai no padrão
    percentualAntecipado: s(p?.percentualAntecipado) || String(PCT_ANTECIPADO_PADRAO),
  };
}

function Secao({ children, cor = VERDE }: { children: React.ReactNode; cor?: string }) {
  return (
    <div style={{ fontSize: 13, fontWeight: 700, color: cor, marginBottom: 12, textTransform: "uppercase", letterSpacing: 1 }}>
      {children}
    </div>
  );
}

const LINHA: React.CSSProperties = { display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 20 };
const CELULA: React.CSSProperties = { flex: "1 1 48%", minWidth: 220 };

/**
 * Bloco com fundo próprio, para a parte de cima do formulário.
 *
 * Antes era uma lista corrida de dezesseis campos sob um único título
 * "Visitante": o nome do aluno ficava embaixo do e-mail do pai, e a escola de
 * procedência no meio dos dados da visita. Separar em quatro caixas coloridas
 * dá ao olho um lugar para pousar — a pessoa procura "dados do aluno" e acha a
 * caixa rosa, sem ler campo por campo.
 */
function Bloco({
  titulo, cor, fundo, borda, children,
}: {
  titulo: string; cor: string; fundo: string; borda: string; children: React.ReactNode;
}) {
  return (
    <div style={{ background: fundo, border: `1px solid ${borda}`, borderRadius: 12, padding: "14px 16px 0", marginBottom: 16 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: cor, marginBottom: 12, textTransform: "uppercase", letterSpacing: 1 }}>
        {titulo}
      </div>
      {children}
    </div>
  );
}

/**
 * Aviso de piso comercial, logo abaixo do campo que furou.
 *
 * Mostra também qual é o piso: "abaixo do mínimo" sem o número obriga a pessoa
 * a sair da tela para descobrir de quanto ela precisa, no meio do atendimento.
 */
function AbaixoDoMinimo({ furou, minimo }: { furou: boolean; minimo: number | null }) {
  if (!furou) return null;
  return (
    <div
      role="alert"
      style={{
        marginTop: 6, background: "#fef3f2", border: "1px solid #fda29b", borderRadius: 8,
        padding: "8px 10px", fontSize: 11.5, color: "#b42318", fontWeight: 600, lineHeight: 1.5,
      }}
    >
      {AVISO_ABAIXO_DO_MINIMO}
      {minimo !== null && (
        <div style={{ fontWeight: 400, marginTop: 3 }}>
          Mínimo para esta mensalidade: <strong>{moeda(minimo)}</strong>
        </div>
      )}
    </div>
  );
}

/**
 * A linha que traduz a política de pontualidade em dois números.
 *
 * O boleto sai pelo valor de cima; pagando até o vencimento, a família abate o
 * desconto e chega no valor contratado. Fica abaixo do campo do valor
 * contratado porque é a leitura dele — não um valor novo para conferir.
 */
function Pontualidade({
  exclusiva, boleto, desconto,
}: { exclusiva: string; boleto: string; desconto: string }) {
  if (!boleto || !desconto || Number(desconto) <= 0) return null;
  const linha = (rotulo: string, valor: string, forte = false) => (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
      <span>{rotulo}</span>
      <strong style={{ fontWeight: forte ? 700 : 500, whiteSpace: "nowrap" }}>{valor}</strong>
    </div>
  );
  return (
    <div style={{ fontSize: 11, color: "#1a5c2e", background: "#f0faf4", border: "1px solid #c6e9d4", borderRadius: 6, padding: "6px 8px", marginTop: 6, lineHeight: 1.7 }}>
      {linha("Condição exclusiva", `− ${moeda(exclusiva)}`)}
      {linha("Valor do boleto", moeda(boleto), true)}
      {linha("Benefício pagamento antecipado", `− ${moeda(desconto)}`)}
    </div>
  );
}

export function FormularioVisita({
  visita,
  apoio,
  orcamento = false,
  onSalvar,
  onCancelar,
}: {
  visita?: Visita | null;
  apoio: DadosApoio;
  /**
   * Orçamento: mesma proposta, sem encontro marcado. Somem os campos que só
   * fazem sentido com a família presente — data e hora da visita, status,
   * motivo da não matrícula. Deixá-los na tela convidaria a preencher uma
   * agenda que não existe, que é exatamente o remendo que isto veio substituir.
   */
  orcamento?: boolean;
  onSalvar: (f: EstadoFormulario) => Promise<void>;
  onCancelar: () => void;
}) {
  const novo = !visita;
  const orc = orcamento || visita?.tipo === "ORCAMENTO";
  const [f, setF] = useState<EstadoFormulario>(() =>
    estadoInicial(visita, orcamento ? "ORCAMENTO" : "VISITA"),
  );
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState("");

  /**
   * A política de pontualidade vem do servidor e só vale para proposta nova.
   * Visita antiga mantém o percentual que estava valendo quando foi feita —
   * senão a ficha reimpressa amanhã mostraria números diferentes dos que a
   * família levou para casa.
   */
  useEffect(() => {
    const pct = apoio.descontoAntecipadoPct;
    if (pct === null || visita?.proposta?.percentualAntecipado) return;
    setF((prev) =>
      prev.percentualAntecipado === String(pct)
        ? prev
        : (recalcular({ ...prev, percentualAntecipado: String(pct) }) as EstadoFormulario),
    );
  }, [apoio.descontoAntecipadoPct, visita]);

  /** Toda alteração passa pelo recálculo — mesma cascata do 4.x. */
  const set = (campo: keyof EstadoFormulario) => (valor: string) =>
    setF((prev) => recalcular({ ...prev, [campo]: valor }) as EstadoFormulario);

  /**
   * Escolher o valor normal preenche também a matrícula, que na prática é o
   * mesmo número quase sempre — digitar duas vezes só cria oportunidade de
   * errar em uma delas.
   *
   * A matrícula **para de acompanhar** no instante em que alguém digita nela um
   * valor diferente, e volta a acompanhar se o campo for esvaziado. Descobrir
   * isso comparando com o valor normal anterior evita guardar um "está
   * seguindo?" em algum lugar — estado escondido que sobreviveria ao salvar,
   * sumiria ao reabrir a visita e daria comportamento diferente nas duas vezes.
   */
  const setValorNormal = (campoNormal: "valorNormal" | "valorNormalIntegral") => (valor: string) => {
    const campoMatricula = campoNormal === "valorNormal" ? "valorMatricula" : "valorMatriculaIntegral";
    setF((prev) => {
      const acompanhando = prev[campoMatricula] === "" || prev[campoMatricula] === prev[campoNormal];
      return recalcular({
        ...prev,
        [campoNormal]: valor,
        ...(acompanhando ? { [campoMatricula]: valor } : {}),
      }) as EstadoFormulario;
    });
  };

  /** O estado guarda o telefone já mascarado; `paraPayload` tira a máscara. */
  const setTelefone = (valor: string) =>
    setF((prev) => ({ ...prev, telefoneResponsavel: mascaraPorPais(valor, prev.paisTelefone) }));

  /**
   * Trocar o país remascara o número que já está digitado.
   *
   * Sem isso um "(12) 99609-3693" continuaria com cara de brasileiro depois de
   * mudar para Portugal, e a pessoa teria que apagar e redigitar para entender
   * que o formato mudou. Os dígitos são preservados — ninguém perde o que
   * digitou só por corrigir o país.
   */
  const setPais = (codigo: string) =>
    setF((prev) => ({
      ...prev,
      paisTelefone: codigo,
      telefoneResponsavel: mascaraPorPais(prev.telefoneResponsavel, codigo),
    }));

  /**
   * Curso e série saem da data de nascimento. Mexer na data ou no ano letivo
   * reescreve os dois — é o "cálculo automático". Mexer direto no curso ou na
   * série continua valendo: nada roda por trás depois disso.
   */
  const aplicar = (prev: EstadoFormulario, dataNascimento: string, anoLetivo: string) => {
    const s = sugerirEscolaridade(dataBrParaIso(dataNascimento), Number(anoLetivo));
    const curso = s ? apoio.cursos.find((c) => c.nome === s.curso) : undefined;
    // procura a série dentro do curso sugerido; o nome solto só como reserva,
    // para séries que ainda não foram amarradas a curso nenhum
    const serie = s
      ? apoio.series.find((x) => x.nome === s.serie && x.cursoId === curso?.id) ??
        apoio.series.find((x) => x.nome === s.serie)
      : undefined;
    return {
      ...prev,
      dataNascimento,
      anoLetivo,
      cursoId: curso ? curso.id : prev.cursoId,
      serieId: serie ? serie.id : prev.serieId,
    };
  };

  const setNascimento = (v: string) => setF((prev) => aplicar(prev, mascaraData(v), prev.anoLetivo));
  const setAnoLetivo = (v: string) => setF((prev) => aplicar(prev, prev.dataNascimento, v));

  /** "" enquanto a data estiver incompleta ou não existir no calendário. */
  const nascimentoIso = useMemo(() => dataBrParaIso(f.dataNascimento), [f.dataNascimento]);
  const dataIncompleta = f.dataNascimento.length > 0 && nascimentoIso === "";

  // e-mail é opcional aqui dentro (visita por telefone muitas vezes não tem),
  // então só avisa quando a pessoa digitou algo que não é e-mail
  const emailValido = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.emailResponsavel.trim());
  const emailInvalido = f.emailResponsavel.trim().length > 0 && !emailValido;

  // telefone é opcional no cadastro, mas o que está escrito tem que fechar:
  // número pela metade não recebe WhatsApp e ninguém descobre até o envio falhar
  const brasil = ehBrasil(f.paisTelefone);
  const telefoneIncompleto =
    soDigitos(f.telefoneResponsavel).length > 0 &&
    !telefoneValido(f.telefoneResponsavel, f.paisTelefone);

  const sugestao = useMemo(
    () => sugerirEscolaridade(nascimentoIso, Number(f.anoLetivo)),
    [nascimentoIso, f.anoLetivo],
  );
  const cursoSugerido = sugestao ? apoio.cursos.find((c) => c.nome === sugestao.curso) : undefined;
  const serieSugerida = sugestao ? apoio.series.find((s) => s.nome === sugestao.serie) : undefined;
  // acontece se o deploy ainda não rodou o seed que renomeia as séries
  const semCadastro = Boolean(sugestao) && (!cursoSugerido || !serieSugerida);
  const divergente = Boolean(
    sugestao &&
      ((cursoSugerido && f.cursoId !== cursoSugerido.id) ||
        (serieSugerida && f.serieId !== serieSugerida.id)),
  );

  /** Opções do ano letivo: o da visita, o seguinte e o que já estiver escolhido. */
  const anosLetivos = useMemo(() => {
    const base = new Date(f.inicioEm).getFullYear() || new Date().getFullYear();
    const escolhido = Number(f.anoLetivo);
    const lista = [base, base + 1, Number.isFinite(escolhido) ? escolhido : base];
    return [...new Set(lista)].sort((a, b) => a - b);
  }, [f.inicioEm, f.anoLetivo]);

  const alternarDiferencial = (id: string) =>
    setF((prev) => ({
      ...prev,
      diferenciaisIds: prev.diferenciaisIds.includes(id)
        ? prev.diferenciaisIds.filter((x) => x !== id)
        : [...prev.diferenciaisIds, id],
    }));

  const grade = useMemo(() => apoio.diferenciais.filter((d) => d.tipo === "GRADE"), [apoio.diferenciais]);
  const integral = useMemo(() => apoio.diferenciais.filter((d) => d.tipo === "INTEGRAL"), [apoio.diferenciais]);

  const exigeMotivo = f.status === "OPTOU_OUTRA_ESCOLA" || f.status === "CANCELADO";
  const parcelasMat = parseInt(f.qtdParcelasMaterial, 10);

  /**
   * Piso comercial por curso, dos dois blocos.
   *
   * Conferir aqui, e não só no servidor, é o que faz o alerta aparecer enquanto
   * a família está na mesa — descobrir a recusa depois de fechar a conversa não
   * resolve nada. A trava de verdade continua sendo a da API.
   */
  const minimoParcial = useMemo(
    () => minimoDaMensalidade(apoio.valores, f.valorNormal, f.cursoId),
    [apoio.valores, f.valorNormal, f.cursoId],
  );
  const minimoIntegral = useMemo(
    () => minimoDaMensalidade(apoio.valores, f.valorNormalIntegral, f.cursoId),
    [apoio.valores, f.valorNormalIntegral, f.cursoId],
  );
  const furouParcial = abaixoDoMinimo(f.valorComDesconto, minimoParcial);
  const furouIntegral = abaixoDoMinimo(f.valorComDescontoIntegral, minimoIntegral);
  const foraDaPolitica = furouParcial || furouIntegral;

  /**
   * Só as etapas escolares entram em "Curso Pretendido".
   *
   * "Período Integral", "Bilíngue" e "Semi-Integral" também são cursos no banco,
   * herdados do 4.x, mas servem para etiquetar linhas da tabela de valores — não
   * são onde o aluno estuda. Ofertá-los aqui fazia a lista ter sete opções, e
   * escolher uma delas deixava a série sem par nenhum.
   *
   * O critério é ter série: etapa escolar tem, rótulo de tabela não. Assim o dia
   * em que a escola criar uma etapa nova, ela aparece sozinha, sem mexer aqui.
   */
  const cursosPedagogicos = useMemo(
    () => apoio.cursos.filter((c) => (c.series?.length ?? 0) > 0),
    [apoio.cursos],
  );

  /**
   * As séries do curso escolhido.
   *
   * Sem curso escolhido, mostra todas — é melhor que uma lista vazia sem
   * explicação. E a série já gravada entra sempre, mesmo fora do curso atual:
   * visita antiga (de antes desta amarração) não pode perder a série só porque
   * alguém abriu o formulário para conferir um telefone.
   */
  const seriesDoCurso = useMemo(() => {
    if (!f.cursoId) return apoio.series;
    const doCurso = apoio.series.filter((s) => s.cursoId === f.cursoId);
    const atual = apoio.series.find((s) => s.id === f.serieId);
    if (atual && !doCurso.some((s) => s.id === atual.id)) return [atual, ...doCurso];
    return doCurso;
  }, [apoio.series, f.cursoId, f.serieId]);

  /**
   * Trocar o curso derruba a série que não pertence a ele.
   *
   * Sem isto o formulário guardaria "3ª Série EM" numa visita de Educação
   * Infantil: o select passa a mostrar outra lista, mas o valor antigo continua
   * no estado e vai para o banco no salvamento.
   */
  const setCurso = (cursoId: string) =>
    setF((prev) => {
      const serieCabe = apoio.series.some((s) => s.id === prev.serieId && s.cursoId === cursoId);
      return { ...prev, cursoId, serieId: serieCabe ? prev.serieId : "" };
    });

  async function salvar() {
    setErro("");
    if (f.nomeResponsavel.trim().length < 2) return setErro("Informe o nome do visitante/responsável");
    if (f.nomeAluno.trim().length < 2) return setErro("Informe o nome do aluno");
    if (!f.inicioEm) return setErro("Informe a data e a hora de início da visita");
    // data pela metade seria descartada em silêncio no envio — melhor barrar aqui
    if (dataIncompleta) return setErro("Data de nascimento incompleta ou inexistente — use dd/mm/aaaa");
    if (foraDaPolitica) return setErro(AVISO_ABAIXO_DO_MINIMO);
    setSalvando(true);
    try {
      await onSalvar(f);
    } catch (e) {
      setErro(e instanceof Error ? e.message : "Falha ao salvar");
    } finally {
      setSalvando(false);
    }
  }

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.5)", zIndex: 900, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div style={{ background: "#fff", borderRadius: 16, maxWidth: 860, width: "100%", maxHeight: "94vh", overflow: "auto", boxShadow: "0 24px 80px rgba(0,0,0,.25)" }}>
        <div style={{ padding: "16px 24px", borderBottom: "1px solid #eaecf0", display: "flex", justifyContent: "space-between", alignItems: "center", position: "sticky", top: 0, background: "#fff", zIndex: 2, borderRadius: "16px 16px 0 0" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <img src={LOGO} alt="COC" style={{ width: 34 }} />
            <div>
              <h2 style={{ margin: 0, fontSize: 18, color: VERDE }}>Colégio COCSJC — Unidade SUL</h2>
              <div style={{ fontSize: 11, color: "#1a5c2e", fontStyle: "italic", fontWeight: 500 }}>
                Sua melhor opção em BILÍNGUE — {orc
                  ? novo ? "Novo Orçamento" : "Editar Orçamento"
                  : novo ? "Nova Visita" : "Editar Visita"}
              </div>
            </div>
          </div>
          <button onClick={onCancelar} style={{ background: "none", border: "none", fontSize: 22, cursor: "pointer", color: "#667" }} aria-label="Fechar">✕</button>
        </div>

        <div style={{ padding: "20px 24px" }}>
          {apoio.erro && <div className="erro" style={{ marginBottom: 16 }}>{apoio.erro}</div>}

          {(visita?.origemPublica || visita?.observacoes) && (
            <div style={{ background: "#f0faf4", border: "1px solid #c6e9d4", borderRadius: 10, padding: "12px 14px", marginBottom: 20 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: VERDE, marginBottom: 6 }}>
                {visita.origemPublica ? "Agendamento feito pela família no site" : "Comentário vindo do sistema anterior"}
                {visita.origemPublica && (
                  <span style={{ fontWeight: 400, color: "var(--texto-suave)", marginLeft: 6 }}>
                    · enviado em {dataHora(visita.criadoEm)}
                  </span>
                )}
              </div>
              {visita.observacoes ? (
                <>
                  <div style={{ fontSize: 11, color: "var(--texto-suave)", marginBottom: 4 }}>
                    {visita.origemPublica
                      ? 'O que ela escreveu no campo "Quer nos contar algo antes da visita?":'
                      : 'Campo "comments" do 4.x, só leitura:'}
                  </div>
                  <div style={{ fontSize: 13, color: "#1a5c2e", whiteSpace: "pre-wrap", background: "#fff", border: "1px solid #c6e9d4", borderRadius: 8, padding: "8px 10px" }}>
                    {visita.observacoes}
                  </div>
                </>
              ) : (
                <div style={{ fontSize: 12, color: "var(--texto-suave)" }}>
                  A família não deixou recado. Os demais campos abaixo vieram do formulário dela.
                </div>
              )}
            </div>
          )}

          {/* A — quem veio conversar */}
          <Bloco titulo="A — Visitante / Responsável" cor={VERDE} fundo="#f0faf4" borda="#c6e9d4">
            <div style={LINHA}>
              <div style={{ flex: "2 1 240px" }}><label className="rotulo">Visitante / Responsável</label>
                <input className="campo" value={f.nomeResponsavel} onChange={(e) => set("nomeResponsavel")(e.target.value)} /></div>
              <div style={{ flex: "1 1 130px" }}><label className="rotulo">País</label>
                <select className="campo" value={f.paisTelefone} onChange={(e) => setPais(e.target.value)}>
                  {PAISES.map((p) => (
                    <option key={p.sigla} value={p.codigo}>+{p.codigo} {p.nome}</option>
                  ))}
                </select></div>
              <div style={{ flex: "1 1 170px" }}>
                <label className="rotulo">Telefone</label>
                <input className="campo" inputMode="tel"
                  placeholder={brasil ? "(12) 99999-9999" : "número sem o código do país"}
                  style={telefoneIncompleto ? { borderColor: "#f97066" } : undefined}
                  value={f.telefoneResponsavel} onChange={(e) => setTelefone(e.target.value)} />
                {telefoneIncompleto && (
                  <div style={{ fontSize: 11, color: "#b42318", marginTop: 4 }}>
                    {brasil
                      ? "Confira o DDD e os dígitos — celular tem 11, fixo tem 10."
                      : "Número curto demais para este país."}
                  </div>
                )}
              </div>
              {/*
                O e-mail já era gravado (o formulário do site sempre pede) e já
                saía na ficha impressa — mas não existia na tela, então ninguém
                conseguia ler nem corrigir. Fica ao lado do telefone, que é o
                outro canal de contato.
              */}
              <div style={{ flex: "2 1 240px" }}>
                <label className="rotulo">
                  E-mail
                  {emailValido && (
                    <a href={`mailto:${f.emailResponsavel.trim()}`}
                      style={{ marginLeft: 6, fontSize: 11, color: VERDE, fontWeight: 600, textDecoration: "none" }}>
                      escrever
                    </a>
                  )}
                </label>
                <input className="campo" type="email" inputMode="email" placeholder="familia@exemplo.com"
                  style={emailInvalido ? { borderColor: "#f97066" } : undefined}
                  value={f.emailResponsavel} onChange={(e) => set("emailResponsavel")(e.target.value)} />
                {emailInvalido && (
                  <div style={{ fontSize: 11, color: "#b42318", marginTop: 4 }}>
                    Confira o e-mail — falta o @ ou o domínio.
                  </div>
                )}
              </div>
            </div>
          </Bloco>

          {/* B — sobre quem vai estudar. A data de nascimento vem antes do curso
              de propósito: é ela que sugere curso e série. */}
          <Bloco titulo="B — Dados do Aluno(a)" cor={ROSA} fundo="#fff5f8" borda="#f7d3e0">
            <div style={LINHA}>
              <div style={{ flex: "2 1 240px" }}><label className="rotulo">Nome do Aluno(a)</label>
                <input className="campo" value={f.nomeAluno} onChange={(e) => set("nomeAluno")(e.target.value)} /></div>
              <div style={{ flex: "1 1 150px" }}><label className="rotulo">Data de Nascimento</label>
                <input className="campo" inputMode="numeric" placeholder="dd/mm/aaaa" maxLength={10}
                  style={dataIncompleta ? { borderColor: "#f97066" } : undefined}
                  value={f.dataNascimento} onChange={(e) => setNascimento(e.target.value)} />
                {dataIncompleta && (
                  <div style={{ fontSize: 11, color: "#b42318", marginTop: 4 }}>
                    {f.dataNascimento.length < 10 ? "Faltam dígitos — use dd/mm/aaaa." : "Essa data não existe."}
                  </div>
                )}
              </div>

              {nascimentoIso && (
                <div style={{ flex: "1 1 100%", background: "#fff", border: "1px solid #f7d3e0", borderRadius: 8, padding: "9px 12px", display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8, fontSize: 12, color: "#8a2c50" }}>
                  <span>Idade em 31/03 de</span>
                  <select value={f.anoLetivo} onChange={(e) => setAnoLetivo(e.target.value)}
                    style={{ padding: "3px 6px", fontSize: 12, borderRadius: 6, border: "1px solid #f7d3e0", background: "#fff", color: "#8a2c50", fontWeight: 600 }}>
                    {anosLetivos.map((a) => <option key={a} value={a}>{a}</option>)}
                  </select>
                  {sugestao ? (
                    <span>
                      <strong>{sugestao.idade} {sugestao.idade === 1 ? "ano" : "anos"}</strong>
                      {" → "}{sugestao.curso} · <strong>{sugestao.serie}</strong>
                    </span>
                  ) : (
                    <span style={{ color: "#b54708" }}>fora da faixa atendida — escolha o curso e a série na mão.</span>
                  )}
                  {semCadastro && (
                    <span style={{ color: "#b54708" }}>
                      (essa série ainda não está cadastrada — avise o administrador)
                    </span>
                  )}
                  {divergente && !semCadastro && (
                    <button type="button" onClick={() => setF((prev) => aplicar(prev, prev.dataNascimento, prev.anoLetivo))}
                      style={{ marginLeft: "auto", background: ROSA, color: "#fff", border: "none", borderRadius: 6, padding: "4px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                      Aplicar
                    </button>
                  )}
                </div>
              )}

              <div style={CELULA}><label className="rotulo">Curso Pretendido</label>
                <select className="campo" value={f.cursoId} onChange={(e) => setCurso(e.target.value)}>
                  <option value="">Selecione...</option>
                  {cursosPedagogicos.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
                </select></div>
              <div style={CELULA}><label className="rotulo">Série Pretendida</label>
                <select className="campo" value={f.serieId} onChange={(e) => set("serieId")(e.target.value)}>
                  <option value="">Selecione...</option>
                  {seriesDoCurso.map((s) => <option key={s.id} value={s.id}>{s.nome}</option>)}
                </select>
                {!f.cursoId && (
                  <div style={{ fontSize: 11, color: "var(--texto-suave)", marginTop: 4 }}>
                    Escolha o curso para ver só as séries dele.
                  </div>
                )}
              </div>
              <div style={CELULA}><label className="rotulo">Período Pretendido</label>
                <select className="campo" value={f.periodo} onChange={(e) => set("periodo")(e.target.value)}>
                  <option value="">Selecione...</option>
                  {Object.entries(ROTULO_PERIODO).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select></div>
            </div>
          </Bloco>

          {/* C — de onde a família veio */}
          <Bloco titulo="C — Origem" cor={AMBAR} fundo="#fffaeb" borda="#fedf89">
            <div style={LINHA}>
              <div style={{ flex: "1 1 100%" }}><label className="rotulo">Escola de Procedência</label>
                <input className="campo" value={f.local} onChange={(e) => set("local")(e.target.value)} /></div>
            </div>
          </Bloco>

          {/* D — o que ajuda a negociação, mas não impede de salvar */}
          <Bloco titulo="D — Complementos" cor={LILAS} fundo="#f8f5ff" borda="#ddd0f5">
            <div style={LINHA}>
              <div style={CELULA}><label className="rotulo">Indicação</label>
                <input className="campo" value={f.indicacao} onChange={(e) => set("indicacao")(e.target.value)} /></div>
              <div style={CELULA}><label className="rotulo">Onde Conheceu o COC</label>
                <input className="campo" value={f.ondeConheceu} onChange={(e) => set("ondeConheceu")(e.target.value)} /></div>
              <div style={CELULA}><label className="rotulo">Nome do Irmão(ã)</label>
                <input className="campo" value={f.nomeIrmao} onChange={(e) => set("nomeIrmao")(e.target.value)} /></div>
              <div style={CELULA}><label className="rotulo">Série do Irmão(ã)</label>
                <select className="campo" value={f.serieIrmao} onChange={(e) => set("serieIrmao")(e.target.value)}>
                  <option value="">Selecione...</option>
                  {apoio.series.map((s) => <option key={s.id} value={s.nome}>{s.nome}</option>)}
                </select></div>
            </div>
          </Bloco>

          <Secao>{orc ? "Situação" : "Visita"}</Secao>
          <div style={LINHA}>
            {!orc && (
              <>
                <div style={CELULA}><label className="rotulo">Data/Hora Início</label>
                  <input type="datetime-local" className="campo" value={f.inicioEm} onChange={(e) => set("inicioEm")(e.target.value)} /></div>
                <div style={CELULA}><label className="rotulo">Data/Hora Término</label>
                  <input type="datetime-local" className="campo" value={f.fimEm} onChange={(e) => set("fimEm")(e.target.value)} /></div>
                <div style={CELULA}><label className="rotulo">Status</label>
                  <select className="campo" value={f.status} onChange={(e) => set("status")(e.target.value)}>
                    {Object.entries(ROTULO_STATUS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select></div>
                {exigeMotivo && (
                  <div style={CELULA}><label className="rotulo">Motivo da Não Matrícula</label>
                    <select className="campo" value={f.motivoNaoMatricula} onChange={(e) => set("motivoNaoMatricula")(e.target.value)}>
                      <option value="">Selecione o motivo...</option>
                      {Object.entries(MOTIVOS_NAO_MATRICULA).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select></div>
                )}
              </>
            )}
            {/* A escola de procedência subiu para o bloco C (Origem). */}
            {orc && (
              <div style={{ flex: "1 1 100%", fontSize: 12, color: "var(--texto-suave)" }}>
                Orçamento não tem visita marcada — nada a preencher aqui.
              </div>
            )}
          </div>

          {!orc && f.status === "EM_PESQUISA" && (
            <div style={{ background: "#f4f0ff", border: "1px solid #d9ccff", color: "#553c8b", borderRadius: 8, padding: "10px 12px", fontSize: 12, marginBottom: 20 }}>
              Ao salvar com status <strong>Em pesquisa</strong>, o convite de pesquisa será enviado
              automaticamente pelo Sistema 2 (apenas na primeira vez que a visita entra nesse status).
            </div>
          )}

          <Secao>4a — Valores (Período Parcial)</Secao>
          <div style={LINHA}>
            <CampoValor rotulo="Valor Normal — Mensalidade (R$)" tipo="MENSALIDADE" periodo="PARCIAL"
              valores={apoio.valores}
              cursoId={f.cursoId} valor={f.valorNormal} onChange={setValorNormal("valorNormal")}
              avisoVazio='Nenhuma mensalidade parcial cadastrada. Peça a um administrador para cadastrar em "Valores Mensalidade".' />
            <div style={CELULA}><label className="rotulo">Condição Exclusiva / Pagamento até a data de vencimento (%)</label>
              <input type="number" className="campo" value={f.percentualDesconto} onChange={(e) => set("percentualDesconto")(e.target.value)} /></div>
            <div style={CELULA}><label className="rotulo">Valor com Condição Exclusiva (R$)</label>
              <input className="campo" readOnly value={f.valorComDesconto}
                style={furouParcial
                  ? { background: "#fef3f2", borderColor: "#d92d20", color: "#b42318", fontWeight: 700 }
                  : { background: "#f9fafb" }} />
              <AbaixoDoMinimo furou={furouParcial} minimo={minimoParcial} />
              <Pontualidade exclusiva={f.condicaoExclusiva} boleto={f.valorAposVencimento} desconto={f.descontoAntecipado} /></div>
            <div style={CELULA}><label className="rotulo">Valor Matrícula (R$)</label>
              <input type="number" className="campo" value={f.valorMatricula} onChange={(e) => set("valorMatricula")(e.target.value)} /></div>
            <div style={CELULA}><label className="rotulo">Condição Exclusiva Matrícula / Pagamento até a data de vencimento (%)</label>
              <input type="number" className="campo" value={f.percentualDescontoMatricula} onChange={(e) => set("percentualDescontoMatricula")(e.target.value)} /></div>
            <div style={CELULA}><label className="rotulo">Matrícula c/ Condição Exclusiva (R$)</label>
              <input className="campo" style={{ background: "#f9fafb" }} value={f.valorMatriculaComDesconto} readOnly />
              <Pontualidade exclusiva={f.condicaoExclusivaMatricula} boleto={f.valorMatriculaAposVencimento} desconto={f.descontoAntecipadoMatricula} /></div>
            <div style={CELULA}><label className="rotulo">Qtde Parcelas</label>
              <input type="number" className="campo" value={f.qtdParcelas} onChange={(e) => set("qtdParcelas")(e.target.value)} /></div>
            <div style={CELULA}><label className="rotulo">Investimento Anual (R$)</label>
              <input className="campo" style={{ background: "#f9fafb", fontWeight: 700 }} value={f.valorAnual} readOnly /></div>
          </div>

          <Secao cor={AZUL}>4b — Valores (Período Integral)</Secao>
          <div style={LINHA}>
            <CampoValor rotulo="Valor Normal — Mensalidade Integral (R$)" tipo="MENSALIDADE" periodo="INTEGRAL"
              valores={apoio.valores}
              cursoId={f.cursoId} valor={f.valorNormalIntegral} onChange={setValorNormal("valorNormalIntegral")}
              avisoVazio='Nenhuma mensalidade integral cadastrada. Peça a um administrador para cadastrar em "Valores Mensalidade".' />
            <div style={CELULA}><label className="rotulo">Condição Exclusiva Integral / Pagamento até a data de vencimento (%)</label>
              <input type="number" className="campo" value={f.percentualDescontoIntegral} onChange={(e) => set("percentualDescontoIntegral")(e.target.value)} /></div>
            <div style={CELULA}><label className="rotulo">Valor com Condição Exclusiva Integral (R$)</label>
              <input className="campo" readOnly value={f.valorComDescontoIntegral}
                style={furouIntegral
                  ? { background: "#fef3f2", borderColor: "#d92d20", color: "#b42318", fontWeight: 700 }
                  : { background: "#f9fafb" }} />
              <AbaixoDoMinimo furou={furouIntegral} minimo={minimoIntegral} />
              <Pontualidade exclusiva={f.condicaoExclusivaIntegral} boleto={f.valorAposVencimentoIntegral} desconto={f.descontoAntecipadoIntegral} /></div>
            <div style={CELULA}><label className="rotulo">Valor Matrícula Integral (R$)</label>
              <input type="number" className="campo" value={f.valorMatriculaIntegral} onChange={(e) => set("valorMatriculaIntegral")(e.target.value)} /></div>
            <div style={CELULA}><label className="rotulo">Condição Exclusiva Matrícula Integral / Pagamento até a data de vencimento (%)</label>
              <input type="number" className="campo" value={f.percentualDescontoMatriculaIntegral} onChange={(e) => set("percentualDescontoMatriculaIntegral")(e.target.value)} /></div>
            <div style={CELULA}><label className="rotulo">Matrícula c/ Condição Exclusiva Integral (R$)</label>
              <input className="campo" style={{ background: "#f9fafb" }} value={f.valorMatriculaComDescontoIntegral} readOnly />
              <Pontualidade exclusiva={f.condicaoExclusivaMatriculaIntegral} boleto={f.valorMatriculaAposVencimentoIntegral} desconto={f.descontoAntecipadoMatriculaIntegral} /></div>
            <div style={CELULA}><label className="rotulo">Qtde Parcelas Integral</label>
              <input type="number" className="campo" value={f.qtdParcelasIntegral} onChange={(e) => set("qtdParcelasIntegral")(e.target.value)} /></div>
            <div style={CELULA}><label className="rotulo">Investimento Anual Integral (R$)</label>
              <input className="campo" style={{ background: "#f9fafb", fontWeight: 700 }} value={f.valorAnualIntegral} readOnly /></div>
          </div>

          <Secao>Material</Secao>
          <div style={LINHA}>
            <CampoValor rotulo="Material COC (R$)" tipo="MATERIAL" valores={apoio.valores}
              cursoId={f.cursoId} valor={f.materialCoc} onChange={set("materialCoc")}
              avisoVazio='Nenhum valor cadastrado. Peça a um administrador para cadastrar em "Valores Material".' />
            <CampoValor rotulo="Material Bilíngue (R$)" tipo="BILINGUE" valores={apoio.valores}
              cursoId={f.cursoId} valor={f.materialBilingue} onChange={set("materialBilingue")}
              opcaoZero="Sem bilíngue (R$ 0,00)"
              avisoVazio='Nenhum valor cadastrado. Peça a um administrador para cadastrar em "Valores Bilíngue".' />
            <div style={CELULA}><label className="rotulo">Total do Material (R$)</label>
              <input className="campo" style={{ background: "#f9fafb" }} value={f.totalMaterial} readOnly /></div>
            <div style={CELULA}><label className="rotulo">Parcelamento Material</label>
              <input type="number" min={1} max={12} className="campo" placeholder="Nº de parcelas"
                value={f.qtdParcelasMaterial} onChange={(e) => set("qtdParcelasMaterial")(e.target.value)} />
              <div style={{ fontSize: 11, color: "#667085", marginTop: 4 }}>
                Até 3x sem acréscimo · 4 a 6x com 5% · 7 a 12x com 12%
              </div>
            </div>
            <div style={CELULA}><label className="rotulo">Valor da Parcela (R$)</label>
              <input className="campo" style={{ background: "#f9fafb" }} value={f.valorParcelaMaterial} readOnly />
              {parcelasMat > 0 && (
                <div style={{ fontSize: 11, marginTop: 4, color: taxaMaterial(parcelasMat) > 0 ? "#667085" : VERDE, fontWeight: taxaMaterial(parcelasMat) > 0 ? 400 : 600 }}>
                  {rotuloTaxaMaterial(parcelasMat)}
                </div>
              )}
            </div>
          </div>

          <Secao>Diferenciais da Grade Curricular — COCSJC Unidade Sul</Secao>
          <Caixa cor={VERDE} fundo="#f0faf4" borda="#c6e9d4" itens={grade} marcados={f.diferenciaisIds} onToggle={alternarDiferencial} />

          <Secao cor={AZUL}>Diferenciais do Período Integral — COCSJC Unidade Sul</Secao>
          <Caixa cor={AZUL} fundo="#eff8ff" borda="#b2d4f0" itens={integral} marcados={f.diferenciaisIds} onToggle={alternarDiferencial} />

          <Secao>Observações da equipe</Secao>
          <textarea className="campo" style={{ resize: "vertical", marginBottom: 20 }} rows={3}
            placeholder="Anotações internas sobre o atendimento"
            value={f.descricao} onChange={(e) => set("descricao")(e.target.value)} />

          {/*
            Repetido aqui embaixo de propósito: o campo que furou fica lá em
            cima, e quem rolou até o botão precisa entender por que ele não
            funciona sem ter que caçar o vermelho na tela.
          */}
          {foraDaPolitica && (
            <div role="alert" style={{ marginBottom: 12, background: "#fef3f2", border: "1px solid #fda29b", borderRadius: 10, padding: "12px 14px", fontSize: 13, color: "#b42318", lineHeight: 1.6 }}>
              <strong>{AVISO_ABAIXO_DO_MINIMO}</strong>
              <div style={{ fontWeight: 400, marginTop: 4 }}>
                {[
                  furouParcial && minimoParcial !== null ? `Parcial: mínimo ${moeda(minimoParcial)}` : "",
                  furouIntegral && minimoIntegral !== null ? `Integral: mínimo ${moeda(minimoIntegral)}` : "",
                ].filter(Boolean).join(" · ")}
                {" — "}ajuste o percentual da condição exclusiva para voltar ao piso.
              </div>
            </div>
          )}

          {erro && <div className="erro" style={{ marginBottom: 12 }}>{erro}</div>}

          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <button className="botao secundario" onClick={onCancelar} disabled={salvando}>Cancelar</button>
            <button
              className="botao"
              onClick={salvar}
              disabled={salvando || foraDaPolitica}
              title={foraDaPolitica ? AVISO_ABAIXO_DO_MINIMO : undefined}
              style={foraDaPolitica ? { opacity: .5, cursor: "not-allowed" } : undefined}
            >
              {salvando ? "Salvando..." : novo ? "Cadastrar Visita" : "Salvar Alterações"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Caixa({
  cor, fundo, borda, itens, marcados, onToggle,
}: {
  cor: string; fundo: string; borda: string;
  itens: Array<{ id: string; nome: string }>;
  marcados: string[];
  onToggle: (id: string) => void;
}) {
  if (itens.length === 0) {
    return <div style={{ fontSize: 12, color: "#667085", marginBottom: 20 }}>Nenhum diferencial cadastrado.</div>;
  }
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 20, background: fundo, borderRadius: 10, padding: "14px 16px", border: `1px solid ${borda}` }}>
      {itens.map((item) => {
        const marcado = marcados.includes(item.id);
        return (
          <label key={item.id} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", flex: "1 1 45%", minWidth: 220, padding: "8px 10px", borderRadius: 8, background: marcado ? "#fff" : "transparent", border: `1px solid ${marcado ? cor : "transparent"}` }}>
            <input type="checkbox" checked={marcado} onChange={() => onToggle(item.id)}
              style={{ width: 18, height: 18, accentColor: cor, cursor: "pointer" }} />
            <span style={{ fontSize: 13, fontWeight: marcado ? 600 : 400, color: marcado ? cor : "#344054" }}>
              {item.nome}
            </span>
          </label>
        );
      })}
    </div>
  );
}

/**
 * O `<input type="datetime-local">` devolve "2026-08-12T09:21" — sem fuso
 * nenhum. Mandar essa string crua para a API grava o horário errado: o Node do
 * Railway roda em UTC e lê "09:21" como 09:21 UTC, que é 06:21 em Brasília.
 *
 * Aqui no navegador a mesma string é interpretada no fuso de quem digitou, e o
 * `toISOString` transforma isso num instante marcado. Aí não há ambiguidade.
 */
const paraIso = (v: string): string | null => {
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
};

/** Converte o estado (tudo string) no corpo que a API espera. */
export function paraPayload(f: EstadoFormulario) {
  const t = (v: string) => (v.trim() === "" ? null : v.trim());
  return {
    nomeResponsavel: f.nomeResponsavel.trim(),
    nomeAluno: f.nomeAluno.trim(),
    // o banco guarda só os dígitos — a máscara é da tela
    telefoneResponsavel: t(soDigitos(f.telefoneResponsavel)),
    paisTelefone: codigoDoPais(f.paisTelefone),
    emailResponsavel: t(f.emailResponsavel),
    dataNascimento: t(dataBrParaIso(f.dataNascimento)),
    inicioEm: paraIso(f.inicioEm),
    fimEm: f.fimEm.trim() ? paraIso(f.fimEm) : null,
    local: t(f.local),
    descricao: t(f.descricao),
    indicacao: t(f.indicacao),
    ondeConheceu: t(f.ondeConheceu),
    cursoId: t(f.cursoId),
    serieId: t(f.serieId),
    periodo: t(f.periodo),
    nomeIrmao: t(f.nomeIrmao),
    serieIrmao: t(f.serieIrmao),
    status: f.status,
    tipo: f.tipo,
    motivoNaoMatricula: t(f.motivoNaoMatricula),
    diferenciaisIds: f.diferenciaisIds,
    proposta: {
      valorNormal: t(f.valorNormal),
      percentualDesconto: t(f.percentualDesconto),
      valorComDesconto: t(f.valorComDesconto),
      valorMatricula: t(f.valorMatricula),
      percentualDescontoMatricula: t(f.percentualDescontoMatricula),
      valorMatriculaComDesconto: t(f.valorMatriculaComDesconto),
      qtdParcelas: t(f.qtdParcelas),
      valorAnual: t(f.valorAnual),
      valorNormalIntegral: t(f.valorNormalIntegral),
      percentualDescontoIntegral: t(f.percentualDescontoIntegral),
      valorComDescontoIntegral: t(f.valorComDescontoIntegral),
      valorMatriculaIntegral: t(f.valorMatriculaIntegral),
      percentualDescontoMatriculaIntegral: t(f.percentualDescontoMatriculaIntegral),
      valorMatriculaComDescontoIntegral: t(f.valorMatriculaComDescontoIntegral),
      qtdParcelasIntegral: t(f.qtdParcelasIntegral),
      valorAnualIntegral: t(f.valorAnualIntegral),
      materialCoc: t(f.materialCoc),
      materialBilingue: t(f.materialBilingue),
      totalMaterial: t(f.totalMaterial),
      qtdParcelasMaterial: t(f.qtdParcelasMaterial),
      valorParcelaMaterial: t(f.valorParcelaMaterial),
      percentualAntecipado: t(f.percentualAntecipado),
    },
  };
}
